import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, MessageCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Field, Select, TextArea, SubmitButton, ThankYou } from "@/components/Field";

const TIMES = ["Morning", "Midday", "Afternoon", "Golden Hour", "Evening", "Flexible"];
const HEAR = ["Instagram", "Google", "Friend / Referral", "Past Client", "Other"];

export default function Book() {
  const [params] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", category: params.get("category") || "",
    preferred_date: "", alternative_date: "", preferred_time: "", num_people: "",
    location: "", instagram: "", message: "", referral: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [settings, setSettings] = useState({});
  const hidden = settings.booking_hidden_fields || [];

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/categories").then((r) => {
      setCategories(r.data);
      const slug = params.get("category");
      if (slug) {
        const match = r.data.find((c) => c.slug === slug);
        if (match) setForm((f) => ({ ...f, category: match.name }));
      }
    }).catch(() => {});
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="book-page" className="pt-32 pb-28">
      <div className="max-w-2xl mx-auto px-6">
        {done ? (
          <ThankYou testId="booking-success"
            text="Thank you! Your request has been received. I'll contact you shortly to confirm availability and details." />
        ) : (
          <>
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#7A7A7A] mb-4">Booking Request</p>
              <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tight mb-4">Request your <span className="italic">session.</span></h1>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-8">
                This is a request, not an instant booking — I'll personally confirm availability and every detail with you.
              </p>
              {settings.contact_phone && (
                <div className="flex gap-3 mb-14" data-testid="quick-contact">
                  <a href={`sms:${settings.contact_phone}`} data-testid="quick-text-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-[#1A1A1A]/20 text-[11px] uppercase tracking-[0.2em] py-4 rounded-full hover:border-[#1A1A1A] transition-colors">
                    <MessageSquare size={14} strokeWidth={1.5} /> Text
                  </a>
                  <a href={`https://wa.me/${String(settings.contact_phone).replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid="quick-whatsapp-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-[#1A1A1A]/20 text-[11px] uppercase tracking-[0.2em] py-4 rounded-full hover:border-[#1A1A1A] transition-colors">
                    <MessageCircle size={14} strokeWidth={1.5} /> WhatsApp
                  </a>
                </div>
              )}
            </Reveal>
            <form onSubmit={submit} className="space-y-8 pb-24 lg:pb-0" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label="First Name" id="booking-first-name" required autoComplete="given-name" value={form.first_name} onChange={set("first_name")} />
                <Field label="Last Name" id="booking-last-name" required autoComplete="family-name" value={form.last_name} onChange={set("last_name")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label="Email" id="booking-email" type="email" required autoComplete="email" inputMode="email" value={form.email} onChange={set("email")} />
                <Field label="Phone Number" id="booking-phone" type="tel" required autoComplete="tel" inputMode="tel" value={form.phone} onChange={set("phone")} />
              </div>
              <Select label="Photography Category" id="booking-category" required
                options={categories.map((c) => c.name)} value={form.category} onChange={set("category")} />
              {(!hidden.includes("preferred_date") || !hidden.includes("alternative_date")) && (
                <div className="grid sm:grid-cols-2 gap-8">
                  {!hidden.includes("preferred_date") && <Field label="Preferred Date" id="booking-preferred-date" type="date" optional value={form.preferred_date} onChange={set("preferred_date")} />}
                  {!hidden.includes("alternative_date") && <Field label="Alternative Date" id="booking-alternative-date" type="date" optional value={form.alternative_date} onChange={set("alternative_date")} />}
                </div>
              )}
              {(!hidden.includes("preferred_time") || !hidden.includes("num_people")) && (
                <div className="grid sm:grid-cols-2 gap-8">
                  {!hidden.includes("preferred_time") && <Select label="Preferred Time" id="booking-preferred-time" optional options={TIMES} value={form.preferred_time} onChange={set("preferred_time")} />}
                  {!hidden.includes("num_people") && <Field label="Number of People" id="booking-num-people" type="number" min="1" inputMode="numeric" optional value={form.num_people} onChange={set("num_people")} />}
                </div>
              )}
              {(!hidden.includes("location") || !hidden.includes("instagram")) && (
                <div className="grid sm:grid-cols-2 gap-8">
                  {!hidden.includes("location") && <Field label="Location / Area" id="booking-location" placeholder="e.g. Santa Monica" optional value={form.location} onChange={set("location")} />}
                  {!hidden.includes("instagram") && <Field label="Instagram Handle" id="booking-instagram" placeholder="@yourhandle" optional value={form.instagram} onChange={set("instagram")} />}
                </div>
              )}
              {!hidden.includes("message") && <TextArea label="Tell me about your session" id="booking-message" optional value={form.message} onChange={set("message")} />}
              {!hidden.includes("referral") && <Select label="How did you hear about me?" id="booking-referral" optional options={HEAR} value={form.referral} onChange={set("referral")} />}
              <div className="pt-6">
                <SubmitButton id="booking-submit" loading={loading}>Request My Session</SubmitButton>
              </div>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
